import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { BenchmarkForm, PropertyCategory } from '../models/ci-form.model';
import { CiQuestion } from '../models/ci-question.model';
import { BenchmarkBasis } from '../models/ci-result.model';
import { CiCalculationResult } from '../../../_services/ci.service';
import { UiInputComponent } from '../ui/ui-input.component';
import { UiNoteBarComponent } from '../ui/ui-note-bar.component';
import {
  formatCompactCurrency,
  formatPsf,
  formatSignedPct,
} from '../../../core/utils/format.util';

/** "04 · Revised Benchmark" for primary and resale units. */
@Component({
  selector: 'ci-step-benchmark',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, UiInputComponent, UiNoteBarComponent],
  templateUrl: './step-benchmark.component.html',
})
export class StepBenchmarkComponent {
  readonly form = input.required<BenchmarkForm>();
  readonly basis = input.required<BenchmarkBasis>();
  readonly questions = input.required<CiQuestion[]>();
  readonly category = input.required<PropertyCategory>();
  /** Latest backend calculation — the only source of every figure below. */
  readonly result = input<CiCalculationResult | null>(null);
  readonly calculating = input(false);
  readonly overrideToggled = output<boolean>();

  psf = formatPsf;
  signedPct = formatSignedPct;
  compact = formatCompactCurrency;

  answerControl(key: string): FormControl<string> {
    return this.form().controls.answers.controls[key];
  }

  baseLabel(): string {
    return this.category() === 'plot-land' ? 'Base Land Rate' : 'Base Benchmark PSF';
  }

  derivedRate(): string {
    const b = this.basis();
    if (b.baseBenchmarkPsf > 0) return formatPsf(b.baseBenchmarkPsf);
    const base = this.form().controls.baseBenchmarkPsf.value;
    return base && base > 0 ? formatPsf(base) : '—';
  }

  basisNote(): string {
    const b = this.basis();
    if (this.category() === 'plot-land' || this.category() === 'independent-house') {
      return this.form().controls.baseBenchmarkPsf.value
        ? 'Land rate from the Land Value step'
        : 'Set a land rate in the Land Value step.';
    }
    if (b.baseBenchmarkPsf <= 0) {
      return 'No benchmark established yet. Set the rate manually below.';
    }
    return b.derivedFromComps
      ? `${b.sampleSize} transaction${b.sampleSize === 1 ? '' : 's'}`
      : 'Set manually';
  }

  totalAdjustment(): string {
    return formatSignedPct(this.totalAdjustmentValue() * 100);
  }

  totalAdjustmentClass(): string {
    return this.weightClass(this.totalAdjustmentValue());
  }

  revisedRate(): string {
    const base = this.form().controls.baseBenchmarkPsf.value ?? this.basis().baseBenchmarkPsf;
    return base && base > 0 ? formatPsf(base * (1 + this.totalAdjustmentValue())) : '—';
  }

  /** Only explicitly selected options contribute; preview API defaults are not selections. */
  private totalAdjustmentValue(): number {
    return this.questions().reduce((total, question) => {
      const selected = this.answerControl(question.key)?.value;
      if (!selected) return total;
      return total + (question.options.find((option) => option.value === selected)?.weight ?? 0);
    }, 0);
  }

  matchingResult(): CiCalculationResult | null {
    const result = this.result();
    if (!result) return null;
    const total = result.totalChecklistWeightagePercent;
    const base = this.form().controls.baseBenchmarkPsf.value ?? this.basis().baseBenchmarkPsf;
    const expected = base * (1 + this.totalAdjustmentValue());
    return total != null && Math.abs(total - this.totalAdjustmentValue()) < 0.000001
      && result.revisedBenchmark != null && Math.abs(result.revisedBenchmark - expected) < 1
      ? result : null;
  }

  weight(w: number): string {
    if (w === 0) return '0.0%';
    return `${w > 0 ? '+' : '−'}${Math.abs(w * 100).toFixed(1)}%`;
  }

  weightClass(w: number | null | undefined): string {
    if (w == null) return 'text-slate-400';
    if (w > 0) return 'text-emerald-600';
    if (w < 0) return 'text-red-600';
    return 'text-slate-400';
  }

  showError(key: string): boolean {
    const c = this.answerControl(key);
    return !!c && c.invalid && (c.touched || c.dirty);
  }

  onOverrideToggle(): void {
    this.overrideToggled.emit(this.form().controls.overrideBase.value);
  }

  isPlot(): boolean { return this.category() === 'plot-land'; }
}
